import { i18n as designSystemI18n } from "@st-marketplace/ui";
import { FormatIcu } from "@tolgee/format-icu";
import { I18nextPlugin, withTolgee } from "@tolgee/i18next";
import type { TolgeeInstance } from "@tolgee/react";
import { BrowserExtensionPlugin, DevBackend, Tolgee } from "@tolgee/react";
import i18next from "i18next";
import ICU from "i18next-icu";
import { getDefaultStore } from "jotai";
import { atomEffect } from "jotai-effect";
import { initReactI18next } from "react-i18next";
import { z } from "zod";
import { makeZodI18nMap } from "zod-i18n-map";

import { LANGUAGES, LANGUAGE_KEYS } from "@/constants";
import type { Language } from "@/constants/language";
import { languageAtom } from "@/store/language";
import type { Writeable } from "@/utils/type-utils";

let _tolgee: TolgeeInstance | null = null;
export const getTolgee = () => {
	if (_tolgee === null) throw new Error("Tolgee is not initialized");

	return _tolgee;
};

const syncDesignSystemLanguage = (language: Language) => {
	designSystemI18n.changeLanguage(language);

	if (typeof document !== "undefined") {
		document.documentElement.lang = language;
		document.documentElement.dir = LANGUAGES[language].mode;
	}
};

export function initTolgee() {
	const fallBackLanguage = getDefaultStore().get(languageAtom);

	syncDesignSystemLanguage(fallBackLanguage);

	const api_config = {
		apiUrl: import.meta.env.VITE_APP_TOLGEE_API_URL,
		apiKey: import.meta.env.VITE_APP_TOLGEE_API_KEY,
	} as const;

	const current_config = Number.parseInt(
		import.meta.env.VITE_APP_TOLGEE_FETCH_FROM_SERVER,
	)
		? api_config
		: static_config;

	_tolgee = Tolgee()
		.use(FormatIcu())
		.use(I18nextPlugin())
		.use(BrowserExtensionPlugin())

		.init({
			availableLanguages: LANGUAGE_KEYS as Writeable<typeof LANGUAGE_KEYS>,
			defaultLanguage: fallBackLanguage,

			...current_config,
		});

	if (import.meta.env.VITE_APP_TOLGEE_ENABLE_INCONTEXT) {
		_tolgee.addPlugin(DevBackend());
	}

	withTolgee(i18next, _tolgee)
		.use(ICU)
		.use(initReactI18next)
		.init({
			// debug: true,
			lng: fallBackLanguage,
			fallbackLng: fallBackLanguage,
			interpolation: {
				escapeValue: false,
			},
			defaultNS: "translation",
			ns: ["translation", "zod"],
			react: {
				useSuspense: false,
			},
		});

	for (const lang of LANGUAGE_KEYS) {
		const zodPath = LANGUAGES[lang].zodPath;
		i18next.addResourceBundle(lang, "zod", zodPath);
	}

	z.setErrorMap(makeZodI18nMap());
}

export const tolgeeLanguageEffect = atomEffect((get) => {
	const currentLanguage = get(languageAtom);
	i18next.changeLanguage(currentLanguage);
	syncDesignSystemLanguage(currentLanguage);
});
tolgeeLanguageEffect.debugLabel = "tolgeeLanguageEffect";

export const static_config = {
	staticData: Object.fromEntries(
		LANGUAGE_KEYS.map((lang) => [
			`${lang}:translation`,
			() =>
				// https://github.com/rollup/plugins/tree/master/packages/dynamic-import-vars#limitations
				import(`../i18n/translation/${lang}.json`),
		]),
	),
} as const;

export { z };
